import {
  Button,
  ColumnProps,
  Table,
  styled,
} from "@monolith-forensics/monolith-ui";
import { GetPreservationsQuery, Preservation } from "../preservations.types";
import { useGetInfinitePreservations } from "../preservations.query";
import { createContext, useContext, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import {
  CreatePreservationModal,
  DeletePreservationModal,
  EditPreservationModal,
} from "../Modals";
import { EditIcon, TrashIcon } from "lucide-react";
import { useClearTransientTableState, useTableState } from "@/Features/_shared";

const DEFAULT_PAGE_SIZE = 100;
const stateStoreKey = "case:preservations:table";

export type PreservationsTableProps = {
  query: GetPreservationsQuery;
};

const ButtonContainer = styled.div`
  display: flex;
  gap: 15px;
`;

const ButtonMenu = ({ preservation }: { preservation: Preservation }) => {
  const {
    setSelectedPreservation,
    setShowDeletePreservationModal,
    setShowEditPreservationModal,
  } = usePreservationTable();
  return (
    <ButtonContainer>
      <Button
        variant="text"
        color="primary"
        size="xxs"
        onClick={() => {
          setSelectedPreservation(preservation);
          setShowEditPreservationModal(true);
        }}
      >
        <EditIcon size={14} />
      </Button>
      <Button
        variant="text"
        color="error"
        size="xxs"
        onClick={() => {
          setSelectedPreservation(preservation);
          setShowDeletePreservationModal(true);
        }}
      >
        <TrashIcon size={14} />
      </Button>
    </ButtonContainer>
  );
};

const PreservationColumnDefs: ColumnProps[] = [
  {
    dataField: "actions",
    caption: "Actions",
    visible: true,
    width: 100,
    render: ({ rowData }) => <ButtonMenu preservation={rowData} />,
  },
  {
    dataField: "uuid",
    caption: "UUID",
    visible: false,
  },
  {
    dataField: "name",
    caption: "Name",
    visible: true,
  },
  {
    dataField: "contact.name",
    caption: "Contact",
    visible: true,

    render: (data: { rowData: Preservation }) => {
      return data?.rowData?.contact?.name ?? "";
    },
  },
  {
    dataField: "contact.email",
    caption: "Contact Email",
    visible: false,

    render: (data: { rowData: Preservation }) => {
      return data?.rowData?.contact?.email ?? "";
    },
  },
  {
    dataField: "contact.organization",
    caption: "Organization",
    visible: false,

    render: (data: { rowData: Preservation }) => {
      return data?.rowData?.contact?.organization ?? "";
    },
  },
  {
    dataField: "case.case_number",
    caption: "Case Number",
    visible: false,

    render: (data: { rowData: Preservation }) => {
      return data?.rowData?.case?.case_number ?? "";
    },
  },
  {
    dataField: "case.case_name",
    caption: "Case Name",
    visible: false,

    render: (data: { rowData: Preservation }) => {
      return data?.rowData?.case?.case_name ?? "";
    },
  },
  {
    dataField: "evidence.name",
    caption: "Evidence Name",
    visible: false,

    render: (data: { rowData: Preservation }) => {
      return data?.rowData?.evidence?.name ?? "";
    },
  },
  {
    dataField: "evidence.evidence_number",
    caption: "Evidence Number",
    visible: false,

    render: (data: { rowData: Preservation }) => {
      return data?.rowData?.evidence?.evidence_number ?? "";
    },
  },
  {
    dataField: "acquisition.name",
    caption: "Acquisition",
    visible: false,

    render: (data: { rowData: Preservation }) => {
      return data?.rowData?.acquisition?.name ?? "";
    },
  },
  {
    dataField: "status",
    caption: "Status",
    visible: true,
    render: (data: { rowData: Preservation }) => {
      return data?.rowData?.status?.name ?? "";
    },
  },
  {
    dataField: "method",
    caption: "Method",
    visible: true,

    render: (data: { rowData: Preservation }) => {
      return data?.rowData?.method?.name ?? "";
    },
  },
  {
    dataField: "trigger_type",
    caption: "Trigger Type",
    visible: false,

    render: (data: { rowData: Preservation }) => {
      return data?.rowData?.trigger_type?.name ?? "";
    },
  },
  {
    dataField: "trigger_date",
    caption: "Trigger Date",
    dataType: "date",
    visible: false,
  },
  {
    dataField: "start_date",
    caption: "Start Date",
    dataType: "date",
    visible: true,
  },
  {
    dataField: "end_date",
    caption: "End Date",
    dataType: "date",
    visible: true,
  },
  {
    dataField: "created_at",
    caption: "Created",
    dataType: "date",
    visible: false,
  },
  {
    dataField: "updated_at",
    caption: "Updated",
    dataType: "date",
    visible: false,
  },
  {
    dataField: "description",
    caption: "Description",
    visible: true,
    render: (data: { rowData: Preservation }) => {
      return (
        <span title={data?.rowData?.description}>
          {data?.rowData?.description}
        </span>
      );
    },
  },
];

const PreservationTableContext = createContext<{
  showCreatePreservationModal: boolean;
  setShowCreatePreservationModal: (show: boolean) => void;
  showEditPreservationModal: boolean;
  setShowEditPreservationModal: (show: boolean) => void;
  showDeletePreservationModal: boolean;
  setShowDeletePreservationModal: (show: boolean) => void;
  selectedPreservation: Preservation | null;
  setSelectedPreservation: (preservation: Preservation | null) => void;
}>({
  showCreatePreservationModal: false,
  setShowCreatePreservationModal: () => {},
  showEditPreservationModal: false,
  setShowEditPreservationModal: () => {},
  showDeletePreservationModal: false,
  setShowDeletePreservationModal: () => {},
  selectedPreservation: null,
  setSelectedPreservation: () => {},
});

const PreservationTableProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [showCreatePreservationModal, setShowCreatePreservationModal] =
    useState<boolean>(false);

  const [showEditPreservationModal, setShowEditPreservationModal] =
    useState<boolean>(false);

  const [showDeletePreservationModal, setShowDeletePreservationModal] =
    useState<boolean>(false);

  const [selectedPreservation, setSelectedPreservation] =
    useState<Preservation | null>(null);

  return (
    <PreservationTableContext.Provider
      value={{
        showCreatePreservationModal,
        setShowCreatePreservationModal,
        showEditPreservationModal,
        setShowEditPreservationModal,
        showDeletePreservationModal,
        setShowDeletePreservationModal,
        selectedPreservation,
        setSelectedPreservation,
      }}
    >
      {children}
    </PreservationTableContext.Provider>
  );
};

export const usePreservationTable = () => {
  return useContext(PreservationTableContext);
};

export const PreservationsTableWrapper: React.FC<PreservationsTableProps> = ({
  query,
}) => {
  const {
    showCreatePreservationModal,
    setShowCreatePreservationModal,
    showDeletePreservationModal,
    setShowDeletePreservationModal,
    selectedPreservation,
    setSelectedPreservation,
    showEditPreservationModal,
    setShowEditPreservationModal,
  } = usePreservationTable();

  // The stateStoreKey is global so the column layout follows the user across
  // cases, but a search typed in one case must not restore in another.
  useClearTransientTableState(stateStoreKey);
  const tableState = useTableState(query, stateStoreKey);

  const debouncedSearch = useDebouncedCallback((value) => {
    tableState.setSearchState(value);
  }, 300);

  // GetPreservationsSchema is strict, so pick the state slices explicitly
  // instead of spreading tableState.resolvedQuery (which adds pageSize/filter).
  const _query: GetPreservationsQuery = {
    ...query,
    page_size: DEFAULT_PAGE_SIZE,
    search: tableState.search,
    sort_by: tableState.sortState?.dataField,
    sort_dir: tableState.sortState?.dir,
  };

  const {
    data,
    isLoading,
    isFetched,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetInfinitePreservations(_query);

  const debouncedFetchNextPage = useDebouncedCallback((value) => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, 50);

  const records = data?.pages.flatMap((page) => page.data) || [];
  const totalRecords = data?.pages[0]?.total || 0;
  const loading = isLoading || !isFetched || isFetchingNextPage;

  return (
    <>
      {showCreatePreservationModal && (
        <CreatePreservationModal
          show={showCreatePreservationModal}
          onClose={() => setShowCreatePreservationModal(false)}
          onSubmit={() => setShowCreatePreservationModal(false)}
        />
      )}
      {showDeletePreservationModal && selectedPreservation && (
        <DeletePreservationModal
          open={showDeletePreservationModal}
          onClose={() => setShowDeletePreservationModal(false)}
          onDelete={() => {
            setShowDeletePreservationModal(false);
            setSelectedPreservation(null);
          }}
          preservationInfo={selectedPreservation}
        />
      )}
      {showEditPreservationModal && selectedPreservation && (
        <EditPreservationModal
          open={showEditPreservationModal}
          onClose={() => setShowEditPreservationModal(false)}
          onEdit={() => {
            setShowEditPreservationModal(false);
            setSelectedPreservation(null);
          }}
          preservation={selectedPreservation}
        />
      )}
      <Table
        data={records}
        loading={loading}
        columnProps={PreservationColumnDefs}
        virtualized={true}
        totalRecords={totalRecords}
        enableColumnResize
        enableColumnReorder
        enableSorting
        manualSearch={true}
        manualSorting={true}
        onColumnHeaderClick={({ sort }) =>
          tableState.setSortState(sort || undefined)
        }
        onScrollToBottom={debouncedFetchNextPage}
        stateStorage={{
          enabled: true,
          type: "localStorage",
          key: stateStoreKey,
        }}
        tableMenuOptions={{
          enabled: true,
          columnSelectorOptions: {
            enabled: true,
          },
          tableCountOptions: {
            recordsTotalEnabled: true,
          },
          addButtonOptions: {
            enabled: true,
            onClick: () => setShowCreatePreservationModal(true),
            label: "Create Preservation",
          },
          searchOptions: {
            enabled: true,
            placeholder: "Search Preservations",
            onSearch: debouncedSearch,
          },
        }}
      />
    </>
  );
};

export const PreservationsTable: React.FC<PreservationsTableProps> = (
  props
) => {
  return (
    <PreservationTableProvider>
      <PreservationsTableWrapper {...props} />
    </PreservationTableProvider>
  );
};
